import { useState } from "react";
import { useTranslation } from "react-i18next";
import { css, cx } from "../../../../../styled-system/css";
import { Flex, HStack, VStack } from "../../../../../styled-system/jsx";
import { Modal } from "../../../../components/core/basic/Modal";
import { Button } from "../../../../components/core/basic/Button";
import TrellisLink from "../../../../components/core/basic/TrellisLink";
import { useAppSelector } from "../../../../redux/hooks";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { useAmcAdvertisers } from "../../api/useAmcAdvertisers";
import { useMarketplaceIdLabel } from "../../utils/utils";

const entityId = css({
  fontSize: "small",
  color: "mauve",
});

const eyebrow = css({
  fontSize: "xsmall",
  fontWeight: "600",
  letterSpacing: "0.15em",
  textTransform: "uppercase",
  color: "mauve",
});

const columnHeader = css({
  fontSize: "xsmall",
  fontWeight: "600",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "text.secondary",
});

const rowGrid = css({
  display: "grid",
  gridTemplateColumns: "1.1fr 1fr 1.5fr",
  gap: "medium",
  alignItems: "center",
});

export function AmcInstanceDetails() {
  const { t } = useTranslation(["amc"]);
  const [isOpen, setIsOpen] = useState(false);

  const hasAmcDetails = useModuleEnabled("AMC_DETAILS");
  const { sponsoredAds, dsp, instance, isError } = useAmcAdvertisers();
  const marketplaceIdLabel = useMarketplaceIdLabel();
  const isStaff = useAppSelector((state) => state.user.is_staff);

  const instanceId = instance?.instance_id;

  if (!hasAmcDetails || !instanceId) {
    return null;
  }

  const resolveMarketplace = (marketplaceId: string | null | undefined) =>
    marketplaceId ? marketplaceIdLabel(marketplaceId) : "-";

  return (
    <>
      <Button
        variant={"link"}
        onPress={() => setIsOpen(true)}
        className={css({ whiteSpace: "nowrap", fontSize: "medium" })}
      >
        {t(($) => $.amc["Instance Details"])}
      </Button>

      <Modal isOpen={isOpen} onOpenChange={setIsOpen} css={{ width: "620px", maxWidth: "92vw", paddingY: "medium" }}>
        <VStack alignItems={"stretch"} gap={"0"} className={css({ minHeight: "0", overflow: "hidden" })}>
          <VStack
            alignItems={"stretch"}
            gap={"small"}
            className={css({ paddingX: "large", paddingBottom: "medium", borderBottomWidth: "1px" })}
          >
            <span className={eyebrow}>{t(($) => $.amc["Reporting on data from"])}</span>

            <Flex alignItems={"center"} gap={"medium"} wrap={"wrap"} className={css({ marginRight: "xlarge" })}>
              <span
                className={css({ fontFamily: "brand", fontSize: "xlarge", fontWeight: "600", color: "text.heading" })}
              >
                {instance.raw.instanceName}
              </span>

              <HStack
                gap={"small"}
                className={css({
                  backgroundColor: "nav",
                  borderWidth: "1px",
                  borderRadius: "light",
                  paddingX: "small",
                  paddingY: "xsmall",
                })}
              >
                <span className={css({ fontSize: "small", color: "text.secondary" })}>
                  {t(($) => $.amc["Instance ID"])}
                </span>
                <span className={css({ fontSize: "small", fontVariantNumeric: "tabular-nums", color: "text.heading" })}>
                  {instanceId}
                </span>
              </HStack>
            </Flex>

            <p className={css({ fontSize: "small", color: "text.secondary", margin: "0", textWrap: "pretty" })}>
              {t(
                ($) =>
                  $.amc[
                    "These are the advertiser accounts and marketplaces feeding your AMC reports. Anything not listed here reports into a separate instance."
                  ],
              )}
            </p>
          </VStack>

          <div className={cx(rowGrid, css({ paddingX: "large", paddingY: "small", borderBottomWidth: "1px" }))}>
            <span className={columnHeader}>{t(($) => $.amc["Advertiser account"])}</span>
            <span className={columnHeader}>{t(($) => $.amc["Marketplace"])}</span>
            <span className={columnHeader}>{t(($) => $.amc["Entity ID"])}</span>
          </div>

          <div className={css({ paddingX: "large", overflowY: "auto", minHeight: "0" })}>
            {isError ?
              <p className={css({ fontSize: "small", color: "error", paddingY: "medium", margin: "0" })}>
                {t(($) => $.amc["Could not load the advertiser accounts for this instance."])}
              </p>
            : sponsoredAds.length === 0 ?
              <p className={css({ fontSize: "small", color: "text.secondary", paddingY: "medium", margin: "0" })}>
                {t(($) => $.amc["No advertiser accounts are attached to this instance yet."])}
              </p>
            : sponsoredAds.map((advertiser) => (
                <div
                  key={advertiser.id}
                  className={cx(
                    rowGrid,
                    css({ paddingY: "small", borderBottomWidth: "1px", _last: { borderBottomWidth: "0" } }),
                  )}
                >
                  <span className={css({ fontSize: "medium", fontWeight: "500", color: "text.heading" })}>
                    {advertiser.name ?? advertiser.id}
                  </span>
                  <span className={css({ fontSize: "medium", color: "text.heading" })}>
                    {resolveMarketplace(advertiser.marketplaceId)}
                  </span>
                  <span className={entityId}>{advertiser.id}</span>
                </div>
              ))
            }

            {dsp.length > 0 && (
              <VStack
                alignItems={"stretch"}
                gap={"small"}
                className={css({ paddingTop: "medium", marginTop: "small", borderTopWidth: "1px" })}
              >
                <span className={columnHeader}>{t(($) => $.amc["DSP entity"])}</span>
                <VStack alignItems={"stretch"} gap={"xsmall"}>
                  {dsp.map((entity) => (
                    <span key={entity.id} className={entityId}>
                      {entity.id}
                    </span>
                  ))}
                </VStack>
              </VStack>
            )}
          </div>

          {isStaff && (
            <Flex
              alignItems={"center"}
              gap={"medium"}
              className={css({
                paddingX: "large",
                paddingTop: "medium",
                marginTop: "medium",
                borderTopWidth: "1px",
              })}
            >
              <span className={css({ flex: "1", fontSize: "small", color: "text.secondary", textWrap: "pretty" })}>
                {t(($) => $.amc["Add another advertiser account or marketplace from Amazon Account Summary."])}
              </span>
              <TrellisLink
                to={"/user/amzsummary"}
                className={css({
                  fontSize: "small",
                  fontWeight: "600",
                  whiteSpace: "nowrap",
                  color: "fuchsia",
                  textDecoration: "none",
                  _hover: { color: "fuchsia", textDecoration: "none", opacity: 0.8 },
                })}
              >
                {t(($) => $.amc["Amazon Account Summary"])}
              </TrellisLink>
            </Flex>
          )}
        </VStack>
      </Modal>
    </>
  );
}
